import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './CartPage.css';

function CartPage() {
  const { cart, addToCart, clearCart, removeFromCart } = useCart();
  const { isLoggedIn, userId } = useAuth();
  const navigate = useNavigate();
  const [showPayPalButton, setShowPayPalButton] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentAccepted, setPaymentAccepted] = useState(false);

  const deliveryCost = cart.some(item => item.deliveryOption === 'Delivery en Santo Domingo') ? 100 : 0;
  const managementFee = cart.reduce((sum, item) => sum + (item.handlingFee || 0) * item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + ((item.ticketPrice || 0) * item.quantity), 0);
  const totalAmount = subtotal + managementFee + deliveryCost;

  useEffect(() => {
    if (showPayPalButton) {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=AWmywecwE4RUc1YxhsEhU_HPcRlVsocN_ft24ALk_qR2YerAwAauj1iLBOwYiC4iDWJkcoUurO7jCtPT&currency=USD&locale=en_US`;

      

      script.addEventListener('load', () => {
        if (window.paypal) {
          window.paypal.Buttons({
            createOrder: (data, actions) => {
              return actions.order.create({
                purchase_units: [
                  { amount: { value: (totalAmount / 60.5).toFixed(2) } }
                ]
              });
            },
            onApprove: (data, actions) => {
              setIsPaying(true);
              return actions.order.capture().then(async (details) => {
                setPaymentAccepted(true);
                const transactionId = details.id;
                const payerName = details.payer.name.given_name;
                const eventId = cart.length > 0 ? cart[0].id : null;

                if (!eventId) {
                  alert("Error: No se pudo determinar el evento.");
                  setIsPaying(false);
                  return;
                }

                const orderPayload = {
                  userId,
                  eventId,
                  tickets: cart.map(item => ({
                    eventAreaId: item.areaId || item.eventAreaId,
                    quantity: item.quantity,
                    deliveryMode: item.deliveryOption === "Delivery en Santo Domingo" ? "physical" : "digital"
                  })).filter(ticket => ticket.eventAreaId !== undefined),
                  payment: {
                    paymentMethod: "PayPal",
                    transactionId,
                    amount: totalAmount
                  }
                };

                try {
                  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/purchase`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(orderPayload)
                  });

                  if (!response.ok) throw new Error("Order API call failed");

                  const orderResponse = await response.json();
                  clearCart();

                  navigate('/resumen', {
                    state: {
                      items: orderResponse.tickets || [],
                      subtotal: orderResponse.totalPrice || 0, 
                      deliveryCost: orderResponse.totalGlobalCharge || 0,
                      totalAmount: (orderResponse.totalPrice + orderResponse.totalGlobalCharge) || 0,
                      payerName,
                      event: orderResponse.eventDTO.title || "Evento"
                    }
                  });

                } catch (error) {
                  alert("Error creando la orden. Intente nuevamente.");
                  setIsPaying(false);
                }
              });
            },
            onError: (err) => {
              setShowPayPalButton(false);
              setIsPaying(false);
              alert("Payment failed. Please try again.");
            },
          }).render('#paypal-button-container');
        }
      });
      document.body.appendChild(script);
      return () => document.body.removeChild(script);
    }
  }, [showPayPalButton, totalAmount, cart, subtotal, managementFee, deliveryCost, clearCart, navigate, userId]);

  const handleProceedToPayment = () => {
    if (!isLoggedIn) {
      navigate('/mi-cuenta');
    } else {
      setIsPaying(true);
      setShowPayPalButton(true);
    }
  };

  return (
    <div className="cart-page">
      <h2>Carrito de Compras</h2>
      {cart.length === 0 ? (
        <p>Tu carrito está vacío.</p>
      ) : (
        <div>
          <div className="cart-items-container">
            {cart.map((item, index) => (
              <div key={index} className="cart-item-card">
                <p><strong>Evento:</strong> {item.title}</p>
                <p><strong>Área:</strong> {item.area}</p>
                <p><strong>Cantidad:</strong> {item.quantity}</p>
                <p><strong>Precio Unitario:</strong> RD${(item.ticketPrice || 0).toFixed(2)}</p>
                <p><strong>Gestión:</strong> RD${(item.handlingFee || 0).toFixed(2)}</p>
                <p><strong>Tipo Ticket:</strong> {item.deliveryOption}</p>
                <p><strong>Total:</strong> RD${((item.ticketPrice || 0) + (item.handlingFee || 0)).toFixed(2)}</p>
                <button onClick={() => removeFromCart(item.id)} className="remove-button" disabled={isPaying}>Eliminar</button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="summary-item"><strong>Subtotal:</strong> RD${subtotal.toFixed(2)}</div>
            <div className="summary-item"><strong>Costo de Gestión:</strong> RD${managementFee.toFixed(2)}</div>
            <div className="summary-item"><strong>Costo de entrega:</strong> RD${deliveryCost}</div>
            <div className="summary-item"><strong>Total a pagar:</strong> RD${totalAmount.toFixed(2)}</div>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              * El pago se procesará en dólares estadounidenses. Si PayPal muestra el total en otra moneda local, es solo como referencia y conversión automática de tu banco.
            </p>

            {!isLoggedIn && (
              <button onClick={() => navigate('/mi-cuenta')} className="login-button" disabled={isPaying}>Iniciar Sesión</button>
            )}
            <button onClick={handleProceedToPayment} className="proceed-button" disabled={!isLoggedIn || isPaying}>
              {isPaying ? "Procesando..." : "Proceder con el Pago"}
            </button>
          </div>

          {paymentAccepted && <p className="payment-message">Pago aceptado. En breve confirmamos su orden...</p>}

          {showPayPalButton && (
            <div id="paypal-button-container" className="paypal-button-container"></div>
          )}
        </div>
      )}
    </div>
  );
}

export default CartPage;